<template>
  <RevForm :initial-values :validate @submit="handleSubmit">
    <template #default="{ values, errors }">
      <div class="space-y-32">
        <div class="space-y-16">
          <h3 class="heading-3 mt-24 md:mt-0">
            {{ i18n(translations.setUpYourLineLabel) }}
          </h3>
          <PhonelineCard
            v-model:offer-option="values.offerOption"
            v-model:phone-number="values.phoneNumber"
            v-model:rio-code="values.rioCode"
            :apiErrors
            :errors
            :is-loading
            :price-options="offer?.priceOptions"
          />
        </div>

        <template v-if="!isLoading">
          <div class="space-y-16">
            <h3 class="heading-3">
              {{ i18n(translations.paymentInformationLabel) }}
            </h3>
            <RevCard class="p-24" space-y-8>
              <RevInputText
                id="phoneline-setup-iban"
                v-model="values.iban"
                :description="i18n(translations.IBANDescription)"
                :error="errors.iban || apiErrors.iban"
                :icon="IconBank"
                :label="i18n(translations.IBAN)"
              />
            </RevCard>
          </div>

          <div class="space-y-16">
            <h3 class="heading-3">
              {{ i18n(translations.moreOptionsLabel) }}
            </h3>
            <RevCard class="flex flex-col items-end space-y-8 p-24">
              <RevCheckbox
                :id="values.offerOption"
                v-model="values.childrenProtection"
                class="w-full"
                :image="IconLockLocked"
                :image-props="{
                  alt: i18n(translations.parentalControlIconAlternativeText),
                }"
                variant="full"
              >
                <template #label>
                  {{ i18n(translations.parentalControl) }}
                </template>
                <template #description>
                  {{ i18n(translations.parentalControlInformation) }}
                </template>
              </RevCheckbox>
              <RevLink
                target="_blank"
                :to="i18n(translations.parentalControlLink)"
                >{{ i18n(translations.parentalControlLabel) }}</RevLink
              >
            </RevCard>
          </div>

          <TermsAndConditionsCheckbox
            v-model="values.termsAndConditions"
            :contractual-summary="contractualSummary?.url"
          />

          <p v-if="errors.termsAndConditions" class="text-static-danger-hi">
            {{ errors.termsAndConditions }}
          </p>

          <div class="flex justify-end">
            <RevButton
              :disabled="isSubmitting"
              full-width="adaptive"
              :loading="isSubmitting"
              type="submit"
              variant="primary"
            >
              {{ i18n(translations.submit) }}
            </RevButton>
          </div>
        </template>
      </div>

      <template v-if="!isLoading">
        <FormattedMessage
          class="text-static-default-low caption mt-24 block whitespace-pre-line"
          :definition="translations.returnPolicy"
        >
          <template #callPrice>
            <RevLink target="_blank" :to="i18n(translations.callPriceLink)">
              {{ i18n(translations.callPrice) }}
            </RevLink>
          </template>

          <template #withdrawalForm>
            <RevLink
              target="_blank"
              :to="i18n(translations.withdrawalFormLink)"
            >
              {{ i18n(translations.withdrawalForm) }}
            </RevLink>
          </template>
        </FormattedMessage>

        <RevDivider class="my-16" />

        <BouyguesLogout />

        <ReassuranceItems
          v-if="offer"
          class="mt-32 md:mt-56"
          :selected-mobile-plan="offer"
        />
      </template>
    </template>
  </RevForm>
</template>

<script setup lang="ts">
import { navigateTo, useRoute } from '#imports'
import { computed, ref } from 'vue'

import type { HttpApiError } from '@backmarket/http-api'
import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type { MobilePlanOffer } from '@backmarket/http-api/src/api-specs-b2c-services/mobile-plan/types/mobile-plan-offers'
import ReassuranceItems from '@backmarket/nuxt-layer-checkout/components/ReassuranceItems/ReassuranceItems.vue'
import { useBouyguesStore } from '@backmarket/nuxt-layer-checkout/stores/bouyguesStore'
import { navigateToPaymentPage } from '@backmarket/nuxt-layer-payment/pages/navigateToPaymentPage'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevCheckbox } from '@ds/components/Checkbox'
import { RevDivider } from '@ds/components/Divider'
import {
  FORM_VALID,
  RevForm,
  makeValidate,
  required,
} from '@ds/components/Form'
import { RevInputText } from '@ds/components/InputText'
import { RevLink } from '@ds/components/Link'
import { IconBank } from '@ds/icons/IconBank'
import { IconLockLocked } from '@ds/icons/IconLockLocked'

import { useUser } from '~/scopes/checkout/composables/useUser'
import { CHECKOUT } from '~/scopes/checkout/routes-names'
import { getAPIValidationError } from '~/scopes/checkout/utils/getAPIValidationError'
import { getFormattedPhoneNumber } from '~/scopes/checkout/utils/getFormattedPhoneNumber'
import { validateFrenchPhoneNumber } from '~/scopes/checkout/validators/validateFrenchPhoneNumber'

import TermsAndConditionsCheckbox from '../TermsAndConditionsCheckbox/TermsAndConditionsCheckbox.vue'

import translations from './PhonelineSetupForm.translations'
import type { Values } from './PhonelineSetupForm.types'
import BouyguesLogout from './components/BouyguesLogout/BouyguesLogout.vue'
import PhonelineCard from './components/PhonelineCard/PhonelineCard.vue'

type PhonelineSetup =
  ApiSpecs['/mobile-plan/api/v1/customer-account/phone-line-setup']['GET']['response']

const props = defineProps<{
  offer?: MobilePlanOffer | null
  phonelineSetup: PhonelineSetup | null
  accessToken: string | null
  isLoading: boolean
}>()

const { openErrorToast } = useTheToast()

const user = useUser()
const i18n = useI18n()
const route = useRoute()
const { trackFormSubmit } = useTracking()
const $httpFetch = useHttpFetch()
const logger = useLogger()

const { setPhonelineSetupInformation, setAccessToken } = useBouyguesStore()

const contractualSummary = computed(() =>
  props.offer?.documents.find(
    (document) => document.documentType === 'CONTRACT_SUMMARY',
  ),
)

const isSubmitting = ref(false)
const apiErrors = ref<{
  rioCode?: string
  iban?: string
  phoneNumber?: string
}>({})

const initialValues = computed(() => ({
  offerOption: props.phonelineSetup?.offerOption || 'KEEP_PHONE_NUMBER',
  rioCode: props.phonelineSetup?.rioCode || '',
  iban: props.phonelineSetup?.iban || '',
  phoneNumber:
    props.phonelineSetup?.phoneNumber ||
    getFormattedPhoneNumber(
      MarketCountryCode.FR,
      user.phone.dial,
      user.phone.phone,
    ) ||
    '',
  childrenProtection: props.phonelineSetup?.childrenProtection || false,
  termsAndConditions: false,
}))

const validate = makeValidate<Values>({
  offerOption: required(i18n(translations.inputRequired)),
  iban: required(i18n(translations.inputRequired)),
  phoneNumber: (value, values, ctx) => {
    if (values.offerOption === 'NEW_PHONE_NUMBER') return FORM_VALID

    return validateFrenchPhoneNumber(
      i18n(translations.inputRequired),
      i18n(translations.phoneNumberInputErrorInvalid),
    )(value, values, ctx)
  },
  rioCode: (value, values, ctx) => {
    if (values.offerOption === 'NEW_PHONE_NUMBER') return FORM_VALID

    return required(i18n(translations.inputRequired))(value, values, ctx)
  },
  termsAndConditions: (value) =>
    value === true
      ? FORM_VALID
      : i18n(translations.inputTermsAndConditionRequired),
})

async function handleSubmit(values: Values) {
  isSubmitting.value = true

  if (!props.accessToken) {
    logger.error('No Bouygues access token provided for phoneline setup', {
      owners: ['bot-squad-b2cservices-front'],
    })

    openErrorToast()
    isSubmitting.value = false

    navigateTo({ name: CHECKOUT.SERVICES.BOUYGUES.SIGN_UP })

    return
  }

  try {
    const body: {
      partner: string
      iban: string
      phoneNumber?: string
      rioCode?: string
      offerOption: string
      childrenProtection: boolean
      token: string
    } = {
      partner: 'BOUYGUES_TELECOM',
      iban: values.iban,
      offerOption: values.offerOption,
      childrenProtection: values.childrenProtection,
      token: props.accessToken,
    }

    const keepPhoneNumber = values.offerOption === 'KEEP_PHONE_NUMBER'

    if (keepPhoneNumber) {
      body.phoneNumber = values.phoneNumber
      body.rioCode = values.rioCode
    }

    setPhonelineSetupInformation({
      offerOption: values.offerOption,
      iban: values.iban,
      rioCode: keepPhoneNumber ? values.rioCode : null,
      phoneNumber: keepPhoneNumber ? values.phoneNumber : null,
      childrenProtection: values.childrenProtection,
    })

    setAccessToken(props.accessToken)

    const { eligible: isEligible } = await $httpFetch(
      '/mobile-plan/api/v1/customer-account/phone-line-setup',
      {
        method: 'POST',
        body,
      },
    )

    if (!isEligible) {
      await navigateTo({ name: CHECKOUT.SERVICES.BOUYGUES.NOT_ELIGIBLE })

      return
    }

    trackFormSubmit({
      zone: 'bouygues_phoneline_setup',
      name: 'phoneline_setup',
      page_type: route.name as string,
      offer_option: values.offerOption,
      children_protection: values.childrenProtection,
    })

    await navigateToPaymentPage({ name: CHECKOUT.PAYMENT })
  } catch (err) {
    const httpApiError = err as { status: number; data?: HttpApiError }

    if (httpApiError.status === 400 && httpApiError.data) {
      apiErrors.value.phoneNumber = getAPIValidationError<Values>(
        'phoneNumber',
        '/errors/invalid-format',
        i18n(translations.phoneNumberInputErrorInvalid),
        httpApiError.data.errors,
      )
      apiErrors.value.rioCode = getAPIValidationError<Values>(
        'rioCode',
        '/errors/invalid-format',
        i18n(translations.RIOInputErrorInvalid),
        httpApiError.data.errors,
      )
      const ibanFormatApiError = getAPIValidationError<Values>(
        'iban',
        '/errors/invalid-parameter',
        i18n(translations.IBANInputErrorInvalid),
        httpApiError.data.errors,
      )
      const ibanBankAccountNotAllowedApiError = getAPIValidationError<Values>(
        'iban',
        '/errors/bank-account-type-not-allowed',
        i18n(translations.IBANInputErrorAccountNotAllowed),
        httpApiError.data.errors,
      )
      apiErrors.value.iban =
        ibanFormatApiError || ibanBankAccountNotAllowedApiError

      return
    }

    openErrorToast()
  } finally {
    isSubmitting.value = false
  }
}
</script>
